import React, { useState, useEffect, useRef } from 'react';
import { useOffline } from '../OfflineContext';

const OfflineIndicator = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const bannerRef = useRef(null);
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState('');
  const { queue = [], isSyncing, syncSummary, syncQueue, lastSyncMessage } = useOffline() || {};
  const busy = retrying || isSyncing;
  const retry = async () => {
    setRetrying(true);
    setRetryResult('');
    try {
      const result = await syncQueue();
      setRetryResult(result?.message || 'Upload check finished. Any remaining items are still saved on this device.');
    } catch (error) {
      setRetryResult(`Could not finish checking uploads. ${error.message || 'Please retry.'} Your saved work has been kept.`);
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const updateOffset = () => {
      if (!bannerRef.current) {
        document.body.style.paddingTop = '';
        document.documentElement.style.setProperty('--offline-banner-height', '0px');
        return;
      }
      const height = bannerRef.current.offsetHeight || 0;
      document.body.style.paddingTop = `${height}px`;
      document.documentElement.style.setProperty('--offline-banner-height', `${height}px`);
    };

    updateOffset();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(updateOffset) : null;
    if (bannerRef.current) observer?.observe(bannerRef.current);
    window.addEventListener('resize', updateOffset);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateOffset);
      document.body.style.paddingTop = '';
      document.documentElement.style.setProperty('--offline-banner-height', '0px');
    };
  }, [isOffline, queue.length, isSyncing, retrying, retryResult, syncSummary?.conflict, syncSummary?.failed, syncSummary?.pending, syncSummary?.syncing]);

  const retryingIds = new Set(queue.filter((item) => item.status === 'failed' && item.retryable !== false)
    .map((item) => String(item.clientId || item.id)));
  const reportedRetries = (syncSummary?.items || []).filter((item) =>
    item.status === 'failed' && retryingIds.has(String(item.client_id))).length;
  const needsAttention = Math.max(
    queue.filter((item) => item.status === 'failed' && item.retryable === false).length,
    (syncSummary?.conflict || 0) + (syncSummary?.failed || 0) - reportedRetries,
  );
  const pendingCount = queue.length;
  const localIds = new Set(queue.map((item) => String(item.clientId || item.id)));
  const reportedLocalPending = (syncSummary?.items || []).filter((item) =>
    localIds.has(String(item.client_id)) && ['pending', 'syncing'].includes(item.status)).length;
  const otherPending = Math.max(0, (syncSummary?.pending || 0) + (syncSummary?.syncing || 0)
    - reportedLocalPending);
  const localFailures = queue.filter((item) => item.status === 'failed').length;

  if (!isOffline && pendingCount === 0 && otherPending === 0 && !busy && !retryResult && needsAttention === 0) {
    return null;
  }

  const tone = busy ? 'bg-blue-700' : isOffline ? 'bg-red-700' : needsAttention ? 'bg-amber-600' : 'bg-emerald-700';
  const message = busy
    ? lastSyncMessage || 'Checking saved uploads...'
    : retryResult || (isOffline
    ? `${pendingCount} queued item${pendingCount === 1 ? '' : 's'} saved on this device. Upload will resume on reconnect.`
    : needsAttention
      ? `${needsAttention} sync item${needsAttention === 1 ? '' : 's'} need review. ${queue.find((item) => item.error)?.error || 'Account status may include work from another browser or device.'}`
      : isSyncing
        ? 'Uploading queued field work...'
        : pendingCount > 0
          ? localFailures ? `${localFailures} upload${localFailures === 1 ? '' : 's'} delayed on this device. Temporary failures retry automatically. ${queue.find((item) => item.status === 'failed')?.error || ''}`
            : `${pendingCount} item${pendingCount === 1 ? '' : 's'} saved on this device. Upload will start automatically.`
          : `${otherPending} upload${otherPending === 1 ? '' : 's'} reported elsewhere or awaiting confirmation. Open the originating device to check.`);

  return (
    <div
      ref={bannerRef}
      className={`fixed top-0 left-0 right-0 ${tone} text-white z-50 shadow-md`}
      role="status"
      aria-live="polite"
    >
      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-sm">
        <strong>{busy ? 'Syncing' : isOffline ? 'Offline mode' : needsAttention ? 'Sync review' : pendingCount ? 'Upload pending' : 'Sync status'}</strong>
        <span>{message}</span>
        {pendingCount > 0 && otherPending > 0 && (
          <span>{otherPending} additional account upload{otherPending === 1 ? '' : 's'} reported elsewhere or awaiting confirmation.</span>
        )}
        {!isOffline && pendingCount > 0 && (
          <button
            type="button"
            onClick={retry}
            disabled={busy}
            aria-busy={busy}
            className="rounded border border-white/60 px-2 py-1 text-xs font-semibold hover:bg-white/10"
          >
            {busy ? 'Uploading…' : 'Retry now'}
          </button>
        )}
        {retryResult && !busy && (
          <button type="button" onClick={() => setRetryResult('')} className="rounded border border-white/60 px-2 py-1 text-xs font-semibold">Dismiss result</button>
        )}
      </div>
    </div>
  );
};

export default OfflineIndicator;
